/**
 * Postgres NUMERIC/DECIMAL columns (OID 1700) and BIGINT aggregates like
 * COUNT()/SUM() (OID 20) are serialised by `pg` as strings, and the backend
 * routes hand rows straight to `res.json()` without coercing them. So any
 * field backed by one of those column types arrives here as a string, even
 * though it is conceptually a number.
 *
 * Domain types model those fields as `Numeric`/`NumericValue` so TypeScript
 * forces a coercion at the point of use instead of letting `.toFixed()` or an
 * arithmetic operator blow up at runtime.
 */

/** A wire numeric from a NOT NULL column — never absent, but may be a string. */
export type NumericValue = number | string;

/** A wire numeric from a nullable column. */
export type Numeric = NumericValue | null;

/**
 * Coerce a wire value to a number, or null when it is absent or unparseable.
 * Empty strings and NaN are treated as absent so `!= null` guards work.
 */
export function toNum(value: Numeric | undefined): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Coerce to a number, falling back to `fallback` (default 0) when absent. */
export function toNumOr(value: Numeric | undefined, fallback = 0): number {
  const n = toNum(value);
  return n ?? fallback;
}

/** Format a wire value to fixed decimals, or return `dash` when absent. */
export function formatNum(value: Numeric | undefined, digits = 2, dash = '—'): string {
  const n = toNum(value);
  return n == null ? dash : n.toFixed(digits);
}

/**
 * Format a wire value with locale thousands separators, or `dash` when absent.
 * Coercion matters here: `String.prototype.toLocaleString` exists, so calling
 * it on an uncoerced wire value silently returns the raw digits ("1234.00")
 * instead of a grouped number.
 */
export function formatNumeric(value: Numeric | undefined, dash = '-'): string {
  const n = toNum(value);
  return n == null ? dash : n.toLocaleString();
}
